import { ChevronDown } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";

import { DoclistTreeItem } from "~/api/model";
import { FilteredCommand } from "~/components/FilteredCommand";
import { Button } from "~/components/ui/button";
import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { CategoryItem } from "~/features/chat/types";
import { trackEvent } from "~/utils/analytics";
interface DeleteCategoryDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  item: DoclistTreeItem;
  categories: CategoryItem[];
  onCancel: () => void;
  onDelete: (category: CategoryItem) => void;
}

enum CATEGORY_DELETING_STEP {
  CHOOSE_CATEGORY = 1,
  CONFIRM = 2,
}

export const DeleteCategoryDialog: FC<DeleteCategoryDialogProps> = ({
  open,
  setOpen,
  item,
  categories,
  onCancel,
  onDelete,
}) => {
  const { formatMessage } = useIntl();
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState(false);
  const [value, setValue] = useState<number | null>(
    categories.find((c) => c.isFallback)?.id ?? categories[0].id
  );
  const [currentStep, setCurrentStep] = useState(
    CATEGORY_DELETING_STEP.CHOOSE_CATEGORY
  );
  const { category, sections } = item;
  const handleContinue = () => {
    trackEvent({
      eventName: "app/doc_list/delete_category_modal/continue.button/click",
    });
    setCurrentStep(CATEGORY_DELETING_STEP.CONFIRM);
  };
  const handleConfirm = (value: number | null) => {
    if (!value) return;
    const category = categories.find((c) => c.id === value);
    if (!category) return;
    onDelete(category);
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader className="flex flex-row justify-between items-center">
          <AlertDialogTitle>
            <FormattedMessage
              id="docList.deleteCategory"
              defaultMessage="Delete Category"
            />
          </AlertDialogTitle>
          <span className="text-xs text-muted-foreground !m-0">
            <FormattedMessage
              id="docList.DeleteCategoryDialog.steps"
              defaultMessage="Step {current} of 2"
              values={{
                current: currentStep,
              }}
            />
          </span>
        </AlertDialogHeader>
        <AlertDialogDescription>
          {currentStep === CATEGORY_DELETING_STEP.CHOOSE_CATEGORY ? (
            <FormattedMessage
              id="docList.DeleteCategoryDialog.description"
              defaultMessage="The category {name} contains {count, plural, one {# document} other {# documents}}. To delete a category you need to move the documents"
              values={{
                name: <strong className="uppercase">{category.name}</strong>,
                count: sections.length,
              }}
            />
          ) : (
            <FormattedMessage
              id="docList.DeleteCategoryDialog.areYouSure"
              defaultMessage="Are you sure you want to delete the {old_category} category and move {count, plural, one {# document} other {# documents}} to the {new_category} category?"
              values={{
                old_category: (
                  <strong className="uppercase">{category.name}</strong>
                ),
                new_category: (
                  <strong className="uppercase">
                    {categories.find((c) => c.id === value)?.name}
                  </strong>
                ),
                count: sections.length,
              }}
            />
          )}
        </AlertDialogDescription>
        {currentStep === CATEGORY_DELETING_STEP.CHOOSE_CATEGORY && (
          <div className="flex flex-col gap-2 max-w-[672px]">
            <Label>
              <FormattedMessage
                id="docList.DeleteCategoryDialog.newCategory"
                defaultMessage="Select new category"
              />
            </Label>
            <Popover
              open={openCategoryDropdown}
              onOpenChange={setOpenCategoryDropdown}
              modal={true}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={openCategoryDropdown}
                  className="w-full justify-between uppercase"
                >
                  {value
                    ? categories.find((c) => c.id === value)?.name
                    : categories.find((c) => c.isFallback)?.name}
                  <ChevronDown className="opacity-50  w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="min-w-[465px] p-0 cursor-pointer">
                <FilteredCommand>
                  <CommandInput
                    placeholder={formatMessage({
                      id: "docList.moveToDialog.placeholder",
                      defaultMessage: "Start typing",
                    })}
                  />
                  <CommandList className="max-h-[200px]">
                    <CommandEmpty>
                      <FormattedMessage
                        id="docList.moveToDialog.noFound"
                        defaultMessage="No category found"
                      />
                    </CommandEmpty>
                    <CommandGroup>
                      {categories.map((category) => (
                        <CommandItem
                          key={category.id}
                          value={String(category.id)}
                          keywords={[category.name]}
                          onSelect={(currentValue) => {
                            const selectedCategory = categories.find(
                              (c) => String(c.id) === currentValue
                            );
                            if (selectedCategory) {
                              setValue(selectedCategory.id);
                              setOpenCategoryDropdown(false);
                            }
                          }}
                          className="uppercase"
                        >
                          {category.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </FilteredCommand>
              </PopoverContent>
            </Popover>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          {currentStep === CATEGORY_DELETING_STEP.CHOOSE_CATEGORY ? (
            <Button onClick={handleContinue}>
              <FormattedMessage id="continue" defaultMessage="Continue" />
            </Button>
          ) : (
            <Button onClick={() => handleConfirm(value)} variant="destructive">
              <FormattedMessage
                id="docList.DeleteCategoryDialog.confirm"
                defaultMessage="Confirm"
              />
            </Button>
          )}
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};
